import chalk from 'chalk';
import ora from 'ora';
import type { CacheManager, CacheConfig, CacheStats, CacheItem } from './types';
import { LocalCache } from './local-cache';
import { CloudCache } from './cloud-cache';

export class SynthiaCacheManager implements CacheManager {
  private localCache: LocalCache;
  private cloudCache?: CloudCache;
  private config: CacheConfig;
  private spinner = ora();

  constructor(config: CacheConfig) {
    this.config = config;
    this.localCache = new LocalCache(config.local.dir, config.local);

    if (config.cloud.enabled) {
      this.cloudCache = new CloudCache(config.cloud);
    }
  }

  /**
   * 获取缓存
   */
  async get(key: string): Promise<any> {
    this.spinner.start(`获取缓存: ${key}`);

    try {
      // 根据策略决定获取顺序
      if (this.config.strategy.localFirst) {
        // 本地优先策略
        const localItem = await this.localCache.get(key);
        if (localItem) {
          this.spinner.succeed(`本地缓存命中: ${key}`);
          return localItem.value;
        }

        // 本地未命中，尝试云端
        if (this.cloudCache) {
          const cloudItem = await this.cloudCache.get(key);
          if (cloudItem) {
            // 将云端缓存同步到本地
            await this.localCache.set(key, cloudItem.value, cloudItem.meta);
            this.spinner.succeed(`云端缓存命中并同步到本地: ${key}`);
            return cloudItem.value;
          }
        }
      } else {
        // 云端优先策略
        if (this.cloudCache) {
          const cloudItem = await this.cloudCache.get(key);
          if (cloudItem) {
            // 将云端缓存同步到本地
            await this.localCache.set(key, cloudItem.value, cloudItem.meta);
            this.spinner.succeed(`云端缓存命中并同步到本地: ${key}`);
            return cloudItem.value;
          }
        }

        // 云端未命中，尝试本地
        const localItem = await this.localCache.get(key);
        if (localItem) {
          this.spinner.succeed(`本地缓存命中: ${key}`);
          return localItem.value;
        }
      }

      this.spinner.warn(`缓存未命中: ${key}`);
      return null;
    } catch (error) {
      this.spinner.fail(`获取缓存失败: ${key}`);
      console.error(chalk.red('缓存错误:'), error);
      return null;
    }
  }

  /**
   * 设置缓存
   */
  async set(
    key: string,
    value: any,
    options: Partial<CacheItem['meta']> = {}
  ): Promise<void> {
    this.spinner.start(`设置缓存: ${key}`);

    try {
      // 设置本地缓存
      await this.localCache.set(key, value, options);

      // 如果启用云端缓存且配置了自动同步
      if (this.cloudCache && this.config.strategy.autoSync) {
        await this.cloudCache.set(key, value, options);
      }

      this.spinner.succeed(`缓存设置完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`缓存设置失败: ${key}`);
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 批量设置缓存
   */
  async setBatch(
    items: Array<{
      key: string;
      value: any;
      options?: Partial<CacheItem['meta']>;
    }>
  ): Promise<void> {
    this.spinner.start(`批量设置缓存 (${items.length} 项)`);

    try {
      // 并行设置本地缓存
      const localPromises = items.map(item =>
        this.localCache.set(item.key, item.value, item.options || {})
      );
      await Promise.all(localPromises);

      // 如果启用云端缓存且配置了自动同步
      if (this.cloudCache && this.config.strategy.autoSync) {
        const cloudPromises = items.map(item =>
          this.cloudCache!.set(item.key, item.value, item.options || {})
        );
        await Promise.all(cloudPromises);
      }

      this.spinner.succeed(`批量缓存设置完成 (${items.length} 项)`);
    } catch (error) {
      this.spinner.fail(`批量缓存设置失败`);
      console.error(chalk.red('批量缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 事务性设置缓存
   */
  async setTransaction(
    operations: Array<{
      type: 'set' | 'delete';
      key: string;
      value?: any;
      options?: Partial<CacheItem['meta']>;
    }>
  ): Promise<void> {
    this.spinner.start(`执行缓存事务 (${operations.length} 个操作)`);

    try {
      // 执行本地缓存事务
      for (const operation of operations) {
        if (operation.type === 'set') {
          await this.localCache.set(
            operation.key,
            operation.value!,
            operation.options || {}
          );
        } else if (operation.type === 'delete') {
          await this.localCache.delete(operation.key);
        }
      }

      // 如果启用云端缓存且配置了自动同步
      if (this.cloudCache && this.config.strategy.autoSync) {
        for (const operation of operations) {
          if (operation.type === 'set') {
            await this.cloudCache.set(
              operation.key,
              operation.value!,
              operation.options || {}
            );
          } else if (operation.type === 'delete') {
            await this.cloudCache.delete(operation.key);
          }
        }
      }

      this.spinner.succeed(`缓存事务执行完成 (${operations.length} 个操作)`);
    } catch (error) {
      this.spinner.fail(`缓存事务执行失败`);
      console.error(chalk.red('事务错误:'), error);
      throw error;
    }
  }

  /**
   * 批量获取缓存
   */
  async getBatch(keys: string[]): Promise<Map<string, any>> {
    this.spinner.start(`批量获取缓存 (${keys.length} 项)`);

    try {
      const results = new Map<string, any>();

      // 并行获取本地缓存
      const localPromises = keys.map(async key => {
        const item = await this.localCache.get(key);
        if (item) {
          results.set(key, item.value);
        }
        return { key, item };
      });

      const localResults = await Promise.all(localPromises);

      // 对于本地未命中的缓存，尝试从云端获取
      if (this.cloudCache) {
        const missedKeys = localResults
          .filter(result => !result.item)
          .map(result => result.key);

        if (missedKeys.length > 0) {
          const cloudPromises = missedKeys.map(async key => {
            const item = await this.cloudCache!.get(key);
            if (item) {
              // 将云端缓存同步到本地
              await this.localCache.set(key, item.value, item.meta);
              results.set(key, item.value);
            }
            return { key, item };
          });

          await Promise.all(cloudPromises);
        }
      }

      this.spinner.succeed(
        `批量缓存获取完成 (${keys.length} 项, 命中 ${results.size} 项)`
      );
      return results;
    } catch (error) {
      this.spinner.fail(`批量缓存获取失败`);
      console.error(chalk.red('批量缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 删除缓存
   */
  async delete(key: string): Promise<void> {
    this.spinner.start(`删除缓存: ${key}`);

    try {
      // 删除本地缓存
      await this.localCache.delete(key);

      // 删除云端缓存
      if (this.cloudCache) {
        await this.cloudCache.delete(key);
      }

      this.spinner.succeed(`缓存删除完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`删除缓存失败: ${key}`);
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 批量删除缓存
   */
  async deleteBatch(keys: string[]): Promise<void> {
    this.spinner.start(`批量删除缓存 (${keys.length} 项)`);

    try {
      // 并行删除本地缓存
      const localPromises = keys.map(key => this.localCache.delete(key));
      await Promise.all(localPromises);

      // 如果启用云端缓存且配置了自动同步
      if (this.cloudCache && this.config.strategy.autoSync) {
        const cloudPromises = keys.map(key => this.cloudCache!.delete(key));
        await Promise.all(cloudPromises);
      }

      this.spinner.succeed(`批量缓存删除完成 (${keys.length} 项)`);
    } catch (error) {
      this.spinner.fail(`批量缓存删除失败`);
      console.error(chalk.red('批量缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 清空缓存
   */
  async clear(): Promise<void> {
    this.spinner.start('清空所有缓存...');

    try {
      // 清空本地缓存
      await this.localCache.clear();

      // 清空云端缓存
      if (this.cloudCache) {
        await this.cloudCache.clear();
      }

      this.spinner.succeed('所有缓存已清空');
    } catch (error) {
      this.spinner.fail('清空缓存失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 获取缓存统计
   */
  async getStats(): Promise<CacheStats> {
    this.spinner.start('获取缓存统计...');

    try {
      const localStats = await this.localCache.getStats();

      if (this.cloudCache) {
        const cloudStats = await this.cloudCache.getStats();

        // 合并统计信息
        const combinedStats: CacheStats = {
          totalItems: localStats.totalItems + cloudStats.totalItems,
          totalSize: localStats.totalSize + cloudStats.totalSize,
          hits: localStats.hits + cloudStats.hits,
          misses: localStats.misses + cloudStats.misses,
          hitRate: localStats.hitRate, // 使用本地命中率
          avgAccessTime: localStats.avgAccessTime, // 使用本地平均时间
          topItems: [...localStats.topItems, ...cloudStats.topItems]
            .sort((a, b) => b.accessCount - a.accessCount)
            .slice(0, 10),
        };

        this.spinner.succeed('缓存统计获取完成');
        return combinedStats;
      }

      this.spinner.succeed('本地缓存统计获取完成');
      return localStats;
    } catch (error) {
      this.spinner.fail('获取缓存统计失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 预热缓存
   */
  async warmup(keys: string[]): Promise<void> {
    this.spinner.start(`预热 ${keys.length} 个缓存...`);

    try {
      const warmupPromises = keys.map(async key => {
        try {
          await this.get(key);
        } catch (error) {
          console.warn(chalk.yellow(`预热缓存失败: ${key}`), error);
        }
      });

      await Promise.all(warmupPromises);
      this.spinner.succeed(`缓存预热完成: ${keys.length} 个`);
    } catch (error) {
      this.spinner.fail('缓存预热失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 清理过期缓存
   */
  async cleanup(): Promise<void> {
    this.spinner.start('清理过期缓存...');

    try {
      const keys = await this.localCache.keys();
      let cleanedCount = 0;

      for (const key of keys) {
        const item = await this.localCache.get(key);
        if (!item) {
          // 缓存已过期或损坏，删除
          await this.localCache.delete(key);
          cleanedCount++;
        }
      }

      this.spinner.succeed(`过期缓存清理完成: ${cleanedCount} 个`);
    } catch (error) {
      this.spinner.fail('清理过期缓存失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 导出缓存
   */
  async export(): Promise<Buffer> {
    this.spinner.start('导出缓存...');

    try {
      const keys = await this.localCache.keys();
      const exportData = {
        version: '1.0.0',
        timestamp: Date.now(),
        config: this.config,
        items: [] as Array<{ key: string; item: CacheItem }>,
      };

      for (const key of keys) {
        const item = await this.localCache.get(key);
        if (item) {
          exportData.items.push({ key, item });
        }
      }

      const buffer = Buffer.from(JSON.stringify(exportData, null, 2));
      this.spinner.succeed(`缓存导出完成: ${buffer.length} 字节`);
      return buffer;
    } catch (error) {
      this.spinner.fail('导出缓存失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 导入缓存
   */
  async import(data: Buffer): Promise<void> {
    this.spinner.start('导入缓存...');

    try {
      const importData = JSON.parse(data.toString());

      if (importData.version !== '1.0.0') {
        throw new Error(`不支持的缓存版本: ${importData.version}`);
      }

      let importedCount = 0;
      for (const { key, item } of importData.items) {
        await this.localCache.set(key, item.value, item.meta);
        importedCount++;
      }

      this.spinner.succeed(`缓存导入完成: ${importedCount} 个`);
    } catch (error) {
      this.spinner.fail('导入缓存失败');
      console.error(chalk.red('缓存错误:'), error);
      throw error;
    }
  }
}
